/** @jsx jsx */
import { Children } from 'react';
import { jsx } from '@emotion/core';
import { Themes, useThemeObserver } from '@atlaskit/tokens';

interface ThemedImgProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: { [key in Themes]: string };
}

/**
 * Creates a themed image, which alternates it's display source
 * based on the current theme.
 *
 * Accepts all `<img>` attributes as props.
 *
 * @example
 * <ThemedImg
 *  src={{
 *    light: 'light-image.png',
 *    dark: 'dark-image.png',
 *   }}
 *  width={400}
 *  height={300}
 *  alt="Nice, a themed image"
 * />
 */
const ThemedImg = ({ src, alt, ...rest }: ThemedImgProps) => {
  const theme = useThemeObserver();

  // Fallback to light theme if no theme is set
  const themeSrc = theme ? src[theme] : src['light'];

  return (
    <img
      src={themeSrc}
      css={{ maxWidth: '100%', height: 'auto' }}
      alt={alt || ''}
      {...rest}
    />
  );
};

interface ThemedImgMdxProps extends Omit<ThemedImgProps, 'src'> {
  children: React.ReactElement<
    HTMLImageElement & { originalType: string; transformedType: string }
  >[];
}

/**
 * Creates a themed image in MDX.
 *
 * Due to limitations with the 'gatsby-remark-copy-linked-files' plugin
 * (which copies local files linked to/from Markdown), image sources
 * cannot be extracted from custom components. This workaround requires use of
 * <img> tags to catch the transformed `src` and reroute them to the
 * {@link ThemedImg `ThemedImg` component}`
 *
 * @example
 * <ThemedImgMdx width={400} height={300} alt="Nice, a themed image... but in MDX">
 *   <img src="images/light-image.png" data-theme="light" />
 *   <img src="images/dark-image.png" data-theme="dark" />
 * </ThemedImgMdx>
 */
const ThemedImgMdx = ({ children, ...rest }: ThemedImgMdxProps) => {
  let src = {};
  Children.forEach(children, ({ props }) => {
    if (props.originalType !== 'img') {
      return;
    }

    src[props['data-theme']] = props.src;
  });

  return <ThemedImg src={src as ThemedImgProps['src']} {...rest} />;
};

export default ThemedImg;
export { ThemedImgMdx };
